import { useTranslation } from "react-i18next";
import GridComponent from "../../../../../cubeComponent/html/grid";
import css from "./index.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useRef, useState } from "react";
import { convertDate } from "../../../../../services/luxonHelper";
import Forbidden from "@Pages/Error/Forbidden";
import {
  ColumnsDirective,
  ColumnDirective,
  Page,
  Toolbar,
  Edit,
  Inject,
  Filter,
  VirtualScroll,
  Sort,
  DetailRow,
  Selection,
  ColumnChooser,
  Group,
} from "@syncfusion/ej2-react-grids";
import { inactiveUsersService, inactiveUsersUrl } from "../../../../../services/dataManager";
import ReactDOM from "react-dom/client";
import ButtonComponent from "../../../../../cubeComponent/form/ButtonComponent";
import { BUTTON_TYPES } from "../../../../../cubeComponent/constants/globalVariable";
import ModalStateDialogComponent from "../../../../../cubeComponent/html/modal/ModalWithState";
import { showToastNotification } from "../../../../../actions/toast";
import axiosWrapper from "../../../../../services/axiosWrapper";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR } from "../../../../../services/constants";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";

function InactiveUsers() {
  const { t } = useTranslation();
  const portalSettingInfo = useSelector((state) => state.portalSettingInfo);
  const dataGrid = useRef();
  const dispatch = useDispatch();
  const getUserAvatar = ({ id }) => <UserNameTagComponent id={id} />;
  const [openActivateUserModal, setOpenActivateUserModal] = useState(false);

  const pageSettings = { pageSizes: true, pageSize: 10 };
  const editSettings = {
    allowEditing: false,
    allowAdding: false,
    allowDeleting: false,
    mode: "Dialog",
    showDeleteConfirmDialog: true,
  };

  const queryCellInfo = (args) => {
    if (args.column.field === "deactivatedOn") {
      ReactDOM.createRoot(args.cell).render(convertDate(Number(args.data.deactivatedOn ?? "0"), "dateForText"));
    }
  };

  const toolbarClick = (params) => {
    if (params.item.properties.id === "activate_user") {
      setOpenActivateUserModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenActivateUserModal(false);
  };

  const handleActivateUser = async () => {
    handleCloseModal();
    const userData = {
      action: "activate",
      data: {
        userId: dataGrid.current.getSelectedRecords()[0].id,
      },
    };
    const res = await axiosWrapper.post(inactiveUsersUrl, userData);
    const response = {};
    if (res.data.status) {
      response.content = "Activated user successfully!";
      dataGrid.current.refresh();
    } else {
      response.content = SOMETHING_WENT_WRONG;
      response.type = TOAST_TYPE_ERROR;
    }
    dispatch(showToastNotification(response));
  };

  const usersRowStateSet = (state) => {
    dataGrid.current?.toolbarModule.enableItems(["activate_user"], state);
  };

  const activateUserModal = () => (
    <ModalStateDialogComponent
      padding="var(--size-32)"
      footerContent={
        <div className="flex w-full gap-4">
          <ButtonComponent
            onClick={() => {
              handleActivateUser();
            }}
            buttonType="gradientButton"
          >
            Confirm
          </ButtonComponent>
          <ButtonComponent
            onClick={() => {
              handleCloseModal();
            }}
            buttonType="borderButton"
          >
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
      headerTitle="Mark User as Active"
      width="30%"
      onCancel={handleCloseModal}
      isVisible={openActivateUserModal}
      modelContent={<div className="mb-5 mt-4">{t("Are you sure you want to mark this user as active?")}</div>}
    />
  );

  const activateButtonTemplate = () => (
    <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="activate_user">
      <div className={css.gridButtons}>
        <span data-testid="activateUserBtn">{t("Activate")}</span>
      </div>
    </ButtonComponent>
  );

  const toolbarOptions = [
    { id: "activate_user", template: activateButtonTemplate, disabled: true },
    {
      text: "ColumnChooser",
      htmlAttributes: { "data-testid": "columnChooserProjectButton" },
    },
  ];

  const pendingUsersList = useCallback(
    () => (
      <div className={css.controlPane}>
        <GridComponent
          ref={dataGrid}
          dataSource={inactiveUsersService}
          editSettings={editSettings}
          allowSorting
          allowPaging
          allowFiltering
          filterSettings={{
            type: "Menu",
          }}
          toolbar={toolbarOptions}
          toolbarClick={toolbarClick}
          height="100%"
          className={css.tableWrapper}
          allowGrouping={false}
          rowHeight={48}
          pageSettings={pageSettings}
          queryCellInfo={queryCellInfo}
          customGridStyle
          rowSelected={() => {
            usersRowStateSet(true);
          }}
          rowDeselected={() => {
            usersRowStateSet(false);
          }}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="fullName"
              headerText={t("Username")}
              width="120"
              textAlign="Left"
              filter={Filter}
              template={getUserAvatar}
            />
            <ColumnDirective field="email" headerText={t("Email")} width="120" textAlign="Left" filter={Filter} />
            <ColumnDirective headerText={t("Org")} width="120" textAlign="Left" field="companyName" />
            <ColumnDirective
              field="deactivatedOn"
              headerText={t("Deactivated On")}
              type="date"
              width="180"
              textAlign="Left"
              filter={Filter}
              allowEditing={false}
            />
          </ColumnsDirective>
          <Inject
            services={[
              Page,
              DetailRow,
              Toolbar,
              Edit,
              Filter,
              Sort,
              Group,
              ColumnChooser,
              Selection,
              ColumnChooser,
              VirtualScroll,
            ]}
          />
        </GridComponent>
      </div>
    ),
    [portalSettingInfo],
  );

  return (
    <>
      {!portalSettingInfo?.portalUsers?.isVisible ? (
        <Forbidden />
      ) : (
        <div className={css.controlPane}>
          {activateUserModal()}
          {pendingUsersList()}
        </div>
      )}
    </>
  );
}

export default InactiveUsers;
